import {
  Button,
  Card,
  Descriptions,
  DescriptionsProps,
  Drawer,
  Popover,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message,
  Modal,
  Select,
} from 'antd';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { SegmentationLabel } from 'models/segmentation';
import { AdvancedFilterValue } from 'models/advancedFilters';
import { CoverWrapper, FrameImage } from '../styled';
import SegICIDOverlay from './SegICIDOveraly';
import { BsHandThumbsDown, BsHandThumbsUp, BsShuffle } from 'react-icons/bs';
import { BiMessageSquareEdit } from 'react-icons/bi';
import { AiOutlineExpandAlt } from 'react-icons/ai';
import { ArrowRightOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import { Decision, Review, History } from 'models/reviews';
import {
  useCreateReviewMutation,
  useGetReviewsQuery,
  useDeleteReviewMutation,
  useBulkDeleteReviewsMutation,
} from '../../../redux/slices/reviews';
import { UserContext } from 'services/contexts';
import SingleReviewModal from './SingleReviewModal';
import { COLORS, TYPOGRAPHY_SIZES } from 'theme';
import { Text } from 'theme/typography';
import moment from 'moment';
import { getColor } from 'theme/features';
import './styles.css';
import { config } from 'services/config';
import ExpandedSegmentationImageModal from './ExpandedSegmentationImageModal';
import { useCreateSegmentationLabelMutation } from '../../../redux/slices/views';

interface IProps {
  segmentation: SegmentationLabel;
  frameFile: string;
  review?: Review;
  acceptMe?: boolean;
  allSegmentations: SegmentationLabel[];
  onSegmentationToggle: (segmentationId: string, visible: boolean) => void;
  visibleSegmentations: Set<string>;
  onFrameAccept?: () => void;
  onFrameReject?: () => void;
  frameReviewed?: boolean;
  onReviewChange?: () => void;
  // New: local decision helpers from parent (id = icid)
  onLocalDecision?: (uuid: string, decision: Decision) => void;
  onLocalDecisionRemove?: (id: string) => void;
  getLocalDecision?: (id: string) => Decision | undefined;
  removedReviews?: Record<string, boolean>;
  onMarkRemoved?: (ids: string[]) => void;
  isRejected?: boolean;
  objectType?: 'SEGMENTATION_LABEL' | 'SEGMENTATION_PREDICTION';
  segmentationClassOptions?: AdvancedFilterValue[];
}

const SegmentationItem: FC<IProps> = props => {
  const {
    segmentation,
    frameFile,
    review,
    acceptMe,
    allSegmentations,
    onSegmentationToggle,
    visibleSegmentations,
    onFrameAccept,
    onFrameReject,
    frameReviewed,
    onReviewChange,
    onLocalDecision,
    getLocalDecision,
    removedReviews,
    onMarkRemoved,
    objectType = 'SEGMENTATION_LABEL',
    segmentationClassOptions = [],
  } = props;
  const [decision, setDecision] = useState<Decision>();
  const [_, setColor] = useState<string>('aliceblue');
  const { user } = useContext(UserContext);
  const [createReview] = useCreateReviewMutation();
  const [createSegmentationLabel, { isLoading: isCreatingSegmentationLabel }] =
    useCreateSegmentationLabelMutation();
  const { data: allReviews } = useGetReviewsQuery('');
  const [_deleteReview] = useDeleteReviewMutation();
  const [bulkDeleteReviews] = useBulkDeleteReviewsMutation();
  const [isAcceptModelOpen, setIsAcceptModalOpen] = useState<boolean>(false);
  const [isRejectModelOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [showReverseConfirm, setShowReverseConfirm] = useState<boolean>(false);
  const [comment, setComment] = React.useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [history, setHistory] = useState<History[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddSegmentationModalOpen, setIsAddSegmentationModalOpen] = useState(false);
  const [isExpandedImageModalOpen, setIsExpandedImageModalOpen] = useState(false);
  const [selectedSegmentationClass, setSelectedSegmentationClass] = useState<string>();
  const [isSwapCardiacAnatomyModalOpen, setIsSwapCardiacAnatomyModalOpen] = useState(false);
  const [selectedExistingCardiacAnatomyIcid, setSelectedExistingCardiacAnatomyIcid] =
    useState<string>();
  const [selectedSwapCardiacAnatomyClass, setSelectedSwapCardiacAnatomyClass] = useState<string>();
  const [frameDimensions, setFrameDimensions] = useState({ width: 0, height: 0 });
  const [_hoveredSeg, setHoveredSeg] = useState<{
    id: string;
    x: number;
    y: number;
    label: string;
    color?: string;
  } | null>(null);
  const labelUuids = allSegmentations.map(s => s.uuid);
  const existingSegmentationClasses = React.useMemo(
    () =>
      new Set(
        allSegmentations
          .map(seg => seg.segmentationClassValue)
          .filter(Boolean)
          .map(segmentationClass => segmentationClass.trim().toLowerCase())
      ),
    [allSegmentations]
  );
  const missingSegmentationClassOptions = React.useMemo(
    () =>
      segmentationClassOptions.filter(
        option => !existingSegmentationClasses.has(option.name.trim().toLowerCase())
      ),
    [existingSegmentationClasses, segmentationClassOptions]
  );
  const existingCardiacAnatomyOptions = React.useMemo(
    () =>
      allSegmentations
        .filter(seg => seg.segmentationClassValue)
        .map(seg => ({
          value: seg.icid,
          label: seg.segmentationClassValue,
        })),
    [allSegmentations]
  );
  const swapCardiacAnatomyOptions = React.useMemo(
    () => segmentationClassOptions,
    [segmentationClassOptions]
  );
  const segmentationClassAcronyms = React.useMemo(
    () =>
      new Map(
        segmentationClassOptions.map(option => [
          option.name.trim().toLowerCase(),
          option.acronym || option.name,
        ])
      ),
    [segmentationClassOptions]
  );
  const hasServerReviews =
    (allReviews || []).some(
      r => r.object_type === 'SEGMENTATION_LABEL' && labelUuids.includes(r.object_uuid)
    ) ||
    allSegmentations.some(
      seg =>
        !!seg.isApproved ||
        !!seg.isRejected ||
        (getLocalDecision ? getLocalDecision(seg.icid) !== undefined : false)
    ) ||
    false;
  const isReverseDisabled = (!hasServerReviews && decision === undefined) || isSubmitting;

  // Availability is determined by presence of vector `paths` OR a raster `fileUrl`.
  // Treat a segmentation as available if it contains non-empty `paths` or a `fileUrl` that can be shown.
  const isSegmentationAvailable = (s: SegmentationLabel | any) =>
    (Array.isArray(s?.paths) && s.paths.length > 0) || !!s?.fileUrl;

  /** Opens Labeler for one specific segmentation (correct class + sourceICID per mask). */
  const buildLabelerEditLink = (seg: SegmentationLabel, activeSegmentationClass?: string) => {
    const studyIcid = seg.studyIcid ?? segmentation.studyIcid ?? '';
    const dicomIcid = seg.dicomIcid ?? segmentation.dicomIcid ?? '';
    const frameIcid = seg.frameIcid ?? segmentation.frameIcid ?? '';
    const params = new URLSearchParams({
      isReviewMode: 'true',
      activeSegmentationClass: activeSegmentationClass ?? seg.segmentationClassValue,
      sourceICID: seg.icid,
    });
    return `${config.LABELER_URL}/${studyIcid}/${dicomIcid}/${frameIcid}?${params.toString()}`;
  };

  const buildLabelerAddLink = (segmentationClass: string) => {
    const studyIcid = segmentation.studyIcid ?? '';
    const dicomIcid = segmentation.dicomIcid ?? '';
    const frameIcid = segmentation.frameIcid ?? '';
    const params = new URLSearchParams({
      isReviewMode: 'true',
      activeSegmentationClass: segmentationClass,
    });
    return `${config.LABELER_URL}/${studyIcid}/${dicomIcid}/${frameIcid}?${params.toString()}`;
  };

  const openLabelerEditForSegmentation = (seg: SegmentationLabel) => {
    window.open(buildLabelerEditLink(seg), '_blank');
  };

  const openLabelerAddSegmentation = (segmentationClass: string) => {
    window.open(buildLabelerAddLink(segmentationClass), '_blank');
  };

  const resetSwapCardiacAnatomyModal = () => {
    setIsSwapCardiacAnatomyModalOpen(false);
    setSelectedExistingCardiacAnatomyIcid(undefined);
    setSelectedSwapCardiacAnatomyClass(undefined);
  };

  // Restore reviewData for use in onAccept/onReject
  const reviewData: Review = {
    object_uuid: segmentation.uuid,
    object_icid: segmentation.icid,
    object_type: objectType,
    object_view: 'default',
    object_view_id: 0,
    decision: decision ?? Decision.ACCEPTED,
    comment: '',
    annotation_link: '',
  };

  const handleDicomClick = (): void => {
    const token = localStorage.getItem('AccessToken');
    const dicomBaseUrl = config.ATLAS_URL;
    if (segmentation.dicomIcid) {
      const url = `${dicomBaseUrl}/?dicom=${encodeURIComponent(segmentation.dicomIcid)}${token ? `&t=${encodeURIComponent(token)}` : ''}`;
      window.open(url, '_blank');
    }
  };

  useEffect(() => {
    if (review?.decision && review.decision !== decision) {
      setDecision(review.decision);
    }
    // Simple check to avoid re-setting history if it's the same
    if (review?.history && JSON.stringify(review.history) !== JSON.stringify(history)) {
      setHistory(review.history);
    }
  }, [review, decision, history]);

  useEffect(() => {
    if (decision && decision == Decision.ACCEPTED) {
      setColor('green');
    } else {
      setColor('red');
    }
  }, [decision]);

  useEffect(() => {
    if (acceptMe && decision !== Decision.ACCEPTED && decision !== Decision.REJECTED) {
      onAccept();
    }
  }, [acceptMe]);

  const onClose = () => {
    setOpenDrawer(false);
  };

  const items: DescriptionsProps['items'] = [
    {
      key: '2',
      label: 'Dicom',
      span: { xs: 24, sm: 12, md: 8, lg: 8, xl: 6, xxl: 6 },
      children: (
        <Popover content="Click to view this DICOM on Atlas" trigger="hover">
          <Typography.Link onClick={handleDicomClick} style={{ cursor: 'pointer' }}>
            {segmentation.dicomIcid}
          </Typography.Link>
        </Popover>
      ),
    },
    {
      key: '3',
      label: 'Datasource',
      span: { xs: 24, sm: 12, md: 8, lg: 8, xl: 6, xxl: 6 },
      children: (
        <Tag bordered={false} color={getColor(segmentation.datasourceName)}>
          {segmentation.datasourceName}
        </Tag>
      ),
    },
    {
      key: '4',
      label: 'Frame Index',
      span: { xs: 24, sm: 12, md: 8, lg: 8, xl: 6, xxl: 6 },
      children: <Typography.Text>{segmentation.frameIndex}</Typography.Text>,
    },
    // {
    //   key: '5',
    //   label: 'Status',
    //   span: { xs: 24, sm: 12, md: 8, lg: 8, xl: 6, xxl: 6 },
    //   children: (
    //     <div style={{ display: 'flex', gap: '4px' }}>
    //       {review?.decision === Decision.ACCEPTED && <Tag color="green">Accepted</Tag>}
    //       {review?.decision === Decision.REJECTED && <Tag color="red">Rejected</Tag>}
    //       {!review?.decision && <Tag color="orange">Pending</Tag>}
    //     </div>
    //   ),
    // },
  ];

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const _normalizeSegPath = (raw: string): string => {
    // Backend provides like: "M,346.874,243.29Q,346.875,...". Convert to SVG d:
    // 1) remove comma immediately after command letters
    // 2) replace remaining commas with single spaces
    // 3) collapse whitespace
    return raw
      .replace(/([MLCQZHVSA])\s*,/gi, '$1 ')
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const segmentationCover = () => {
    const hasThumbIndicators = allSegmentations.some(seg => {
      const localD = getLocalDecision ? getLocalDecision(seg.icid) : undefined;
      const wasRemoved = removedReviews ? !!removedReviews[seg.icid] : false;
      return (
        (localD !== undefined && [Decision.ACCEPTED, Decision.REJECTED].includes(localD)) ||
        (!wasRemoved && (!!seg.isApproved || !!seg.isRejected))
      );
    });

    return (
      <CoverWrapper ref={wrapperRef}>
        <Tooltip title="Expand image">
          <Button
            shape="circle"
            size="small"
            aria-label="Expand image"
            className="seg-image-expand-button"
            icon={<AiOutlineExpandAlt style={{ fontSize: '14px' }} />}
            onClick={e => {
              e.stopPropagation();
              setIsExpandedImageModalOpen(true);
            }}
          />
        </Tooltip>
        <div className="segmentation-control-stack segmentation-control-stack--card">
          <div className="segmentation-control-list">
            {allSegmentations.map(seg => {
              const isVisible = visibleSegmentations.has(seg.icid);
              // Prefer local decision if present; otherwise use server flags unless we've marked the review removed locally
              const localD = getLocalDecision ? getLocalDecision(seg.icid) : undefined;
              const wasRemoved = removedReviews ? !!removedReviews[seg.icid] : false;
              const isAccepted =
                localD !== undefined
                  ? localD === Decision.ACCEPTED
                  : !wasRemoved && !!seg.isApproved;
              const isRejected =
                localD !== undefined
                  ? localD === Decision.REJECTED
                  : !wasRemoved && !!seg.isRejected;
              return (
                <div key={seg.icid} className="segmentation-control-row">
                  {(() => {
                    const isAvailable = isSegmentationAvailable(seg);
                    const buttonColor = !isAvailable
                      ? COLORS.lightDark
                      : seg.segmentationColor || undefined;
                    const buttonBackground = isVisible ? buttonColor : '#fff';
                    const buttonTextColor = isVisible ? '#fff' : buttonColor;
                    const segmentationClassFullName =
                      seg.segmentationClassValue?.trim() || 'Segmentation';
                    const segmentationClassKey = segmentationClassFullName.toLowerCase();
                    const acronym = segmentationClassAcronyms.get(segmentationClassKey);
                    const segmentationClassDisplayName = acronym || segmentationClassFullName;
                    const tooltipTitle = isAvailable
                      ? isVisible
                        ? `Hide ${segmentationClassFullName}`
                        : `Show ${segmentationClassFullName}`
                      : 'Segmentation not available';
                    return (
                      <Tooltip title={tooltipTitle}>
                        <Button
                          size="small"
                          className="segmentation-control-button"
                          disabled={!isAvailable}
                          style={{
                            cursor: !isAvailable ? 'not-allowed' : undefined,
                            opacity: !isAvailable ? 0.45 : 1,
                            backgroundColor: buttonBackground,
                            borderColor: buttonColor,
                            color: buttonTextColor,
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            if (!isAvailable) return;
                            onSegmentationToggle(seg.icid, !isVisible);
                          }}
                        >
                          {segmentationClassDisplayName}
                        </Button>
                      </Tooltip>
                    );
                  })()}
                  <Tooltip
                    title={
                      isRejected
                        ? `Edit ${seg.segmentationClassValue || 'Segmentation'} in Labeler`
                        : 'You can only edit rejected segmentations'
                    }
                  >
                    <Button
                      shape="circle"
                      size="small"
                      type="default"
                      disabled={!isRejected}
                      aria-label={`Edit ${seg.segmentationClassValue || 'Segmentation'} in Labeler`}
                      icon={<BiMessageSquareEdit style={{ fontSize: '14px' }} />}
                      onClick={e => {
                        e.stopPropagation();
                        openLabelerEditForSegmentation(seg);
                      }}
                    />
                  </Tooltip>
                  {hasThumbIndicators && (
                    <span className="segmentation-status-slot">
                      {isAccepted && (
                        <Tooltip title="Accepted">
                          <BsHandThumbsUp color="green" style={{ fontSize: '14px' }} />
                        </Tooltip>
                      )}
                      {isRejected && (
                        <Tooltip title="Rejected">
                          <BsHandThumbsDown color="red" style={{ fontSize: '14px' }} />
                        </Tooltip>
                      )}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <FrameImage
          src={frameFile || segmentation.fileUrl || (segmentation as any).file_url || ''}
          alt={`Frame ${segmentation.frameIndex}`}
          onLoad={e => {
            const img = e.target as HTMLImageElement;
            if (
              frameDimensions.width !== img.naturalWidth ||
              frameDimensions.height !== img.naturalHeight
            ) {
              setFrameDimensions({ width: img.naturalWidth, height: img.naturalHeight });
            }
          }}
        />
        <SegICIDOverlay
          frameDimensions={frameDimensions}
          allSegmentations={allSegmentations}
          visibleSegmentations={visibleSegmentations}
          wrapperRef={wrapperRef}
          onHover={seg => {
            // keep local hoveredSeg in sync if parent wants it
            if (seg) setHoveredSeg(seg);
            else setHoveredSeg(null);
          }}
          setFrameDimensions={setFrameDimensions}
          stroke={false}
        />
      </CoverWrapper>
    );
  };

  const onAccept = async () => {
    try {
      setIsSubmitting(true);
      await createReview({ ...reviewData, comment: comment, decision: Decision.ACCEPTED });
      setDecision(Decision.ACCEPTED);
      if (onLocalDecision) onLocalDecision(segmentation.icid, Decision.ACCEPTED);
      setIsAcceptModalOpen(false);
      setColor('green');
      setHistory(prevHistory => [
        {
          object_uuid: reviewData.object_uuid,
          comment: comment,
          timestamp: new Date().toString(),
          technician_email: user.email,
        } as History,
        ...prevHistory,
      ]);
      if (onReviewChange) onReviewChange();
    } catch (error) {
      message.error('Failed to accept review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onReject = async () => {
    setIsSubmitting(true);
    await createReview({ ...reviewData, comment: comment, decision: Decision.REJECTED });
    setDecision(Decision.REJECTED);
    if (onLocalDecision) onLocalDecision(segmentation.icid, Decision.REJECTED);
    setIsRejectModalOpen(false);
    setColor('red');
    setHistory(prevHistory => [
      {
        object_uuid: reviewData.object_uuid,
        comment: comment,
        decision: Decision.REJECTED,
        timestamp: new Date().toString(),
        technician_email: user.email,
      } as History,
      ...prevHistory,
    ]);
    setIsSubmitting(false);
    if (onReviewChange) onReviewChange();
  };

  const historyItems = () => {
    if (history.length > 0) {
      return [...history]
        .sort((a, b) => moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf())
        .map(item => ({
          label: moment(item.timestamp).format('LLL'),
          children: (
            <>
              <Text size={TYPOGRAPHY_SIZES.NORMAL} color={COLORS.darkBlue} weight={600}>
                {item.technician_email}
              </Text>
              <p>{item.comment}</p>
            </>
          ),
          color: item.decision === Decision.ACCEPTED ? 'green' : 'red',
        }));
    }
    return [];
  };

  const handleReverse = () => {
    console.log('SegmentationItem.handleReverse called', {
      segmentationIcids: allSegmentations.map(s => s.icid),
      segmentationUuids: allSegmentations.map(s => s.uuid),
      decision,
      allReviewsCount: (allReviews || []).length,
    });

    const objectUuids = allSegmentations.map(s => s.uuid);
    if (objectUuids.length === 0 && decision === undefined) {
      message.info('Nothing to reverse');
      return;
    }

    // show controlled confirmation modal
    setShowReverseConfirm(true);
  };

  return (
    <>
      {!onFrameAccept && !onFrameReject && (
        <>
          <SingleReviewModal
            isModalOpen={isAcceptModelOpen}
            title="Are you sure you want to accept?"
            setComment={setComment}
            handleOk={onAccept}
            handleCancel={() => {
              setIsAcceptModalOpen(false);
              setIsSubmitting(false);
            }}
            loading={isSubmitting}
          />
          <SingleReviewModal
            isModalOpen={isRejectModelOpen}
            title="Are you sure you want to reject?"
            setComment={setComment}
            handleOk={onReject}
            handleCancel={() => {
              setIsRejectModalOpen(false);
              setIsSubmitting(false);
            }}
            loading={isSubmitting}
          />
        </>
      )}

      <Drawer title={segmentation.icid} onClose={onClose} open={openDrawer}>
        <Timeline mode={'left'} items={historyItems()} />
      </Drawer>

      <Modal
        title="Are you sure you want to reverse this review?"
        open={showReverseConfirm}
        onOk={async () => {
          setShowReverseConfirm(false);
          setIsSubmitting(true);
          try {
            const objectUuids = allSegmentations.map(s => s.uuid);
            console.log(
              'SegmentationItem.handleReverse: bulk deleting reviews for objects',
              objectUuids
            );
            const res = await bulkDeleteReviews({
              object_uuids: objectUuids,
              object_type: 'SEGMENTATION_LABEL',
            }).unwrap();
            const deleted = res?.deleted ?? 0;

            allSegmentations.forEach(seg => {
              if (props.onLocalDecisionRemove) {
                props.onLocalDecisionRemove(seg.icid);
              } else if (onLocalDecision) {
                onLocalDecision(seg.icid, Decision.ACCEPTED);
              }
            });

            setDecision(undefined);
            setColor('aliceblue');
            if (onMarkRemoved) onMarkRemoved(allSegmentations.map(s => s.icid));
            message.success(`Reversed ${deleted} review(s)`);
          } catch (error) {
            console.error('SegmentationItem.handleReverse: bulk delete failed', error);
            message.error('Failed to reverse reviews');
          } finally {
            setIsSubmitting(false);
          }
        }}
        onCancel={() => {
          setShowReverseConfirm(false);
        }}
        okText="Reverse"
        cancelText="Cancel"
        centered
        maskClosable={false}
        confirmLoading={isSubmitting}
      >
        <p>This will remove all accept/reject reviews for the segmentations on this frame.</p>
      </Modal>

      <Modal
        title="Add missing segmentation"
        open={isAddSegmentationModalOpen}
        onCancel={() => {
          setIsAddSegmentationModalOpen(false);
          setSelectedSegmentationClass(undefined);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsAddSegmentationModalOpen(false);
              setSelectedSegmentationClass(undefined);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            disabled={!selectedSegmentationClass}
            onClick={() => {
              if (!selectedSegmentationClass) return;
              openLabelerAddSegmentation(selectedSegmentationClass);
              setIsAddSegmentationModalOpen(false);
              setSelectedSegmentationClass(undefined);
            }}
          >
            Create missing segmentation in Labeler
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary">
          If the cardiac anatomy you are looking for is missing from the dropdown, it should be
          edited instead of creating a new segmentation.
        </Typography.Paragraph>
        <Select
          style={{ width: '100%' }}
          placeholder="Select cardiac anatomy"
          value={selectedSegmentationClass}
          onChange={setSelectedSegmentationClass}
          options={missingSegmentationClassOptions.map(option => ({
            value: option.name,
            label: option.name,
          }))}
          showSearch
          optionFilterProp="label"
        />
      </Modal>

      <Modal
        title="Swap cardiac anatomy"
        open={isSwapCardiacAnatomyModalOpen}
        onCancel={resetSwapCardiacAnatomyModal}
        footer={[
          <Button key="cancel" onClick={resetSwapCardiacAnatomyModal}>
            Cancel
          </Button>,
          <Button
            key="swap-cardiac-anatomy"
            type="primary"
            loading={isCreatingSegmentationLabel}
            disabled={
              isCreatingSegmentationLabel ||
              !selectedExistingCardiacAnatomyIcid ||
              !selectedSwapCardiacAnatomyClass
            }
            onClick={async () => {
              if (!selectedExistingCardiacAnatomyIcid || !selectedSwapCardiacAnatomyClass) return;
              const selectedSegmentation = allSegmentations.find(
                seg => seg.icid === selectedExistingCardiacAnatomyIcid
              );
              if (!selectedSegmentation) return;

              const sourceSegmentationClass =
                selectedSegmentation.segmentationClass ||
                segmentationClassOptions.find(
                  option =>
                    option.name.trim().toLowerCase() ===
                    selectedSegmentation.segmentationClassValue?.trim().toLowerCase()
                )?.uuid;
              const isSourceOwnedByCurrentUser =
                selectedSegmentation.userEmail?.toLowerCase() === user.email?.toLowerCase();

              if (isSourceOwnedByCurrentUser && !sourceSegmentationClass) {
                message.error('Unable to determine the source segmentation class');
                return;
              }

              try {
                await createSegmentationLabel({
                  frame: selectedSegmentation.frameUuid,
                  segmentationClass: selectedSwapCardiacAnatomyClass,
                  paths: selectedSegmentation.paths || [],
                  isFullyVisible: selectedSegmentation.isFullyVisible,
                  sourceUuid: selectedSegmentation.uuid,
                  sourceType: 'SEGMENTATION_LABEL',
                }).unwrap();

                // This mirrors Labeler's Delete control: retain the label record but clear its
                // paths and provenance. Upsert only updates labels belonging to the session user.
                if (isSourceOwnedByCurrentUser && sourceSegmentationClass) {
                  await createSegmentationLabel({
                    uuid: selectedSegmentation.uuid,
                    frame: selectedSegmentation.frameUuid,
                    segmentationClass: sourceSegmentationClass,
                    paths: [],
                    isFullyVisible: selectedSegmentation.isFullyVisible,
                    sourceUuid: null,
                    sourceType: null,
                  }).unwrap();
                }

                message.success(
                  isSourceOwnedByCurrentUser
                    ? 'Created the swapped segmentation label and cleared the original'
                    : 'Created the swapped segmentation label'
                );
                resetSwapCardiacAnatomyModal();
              } catch {
                message.error('Failed to create the swapped segmentation label');
              }
            }}
          >
            Swap cardiac anatomy
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary">
          Swap cardiac anatomy by selecting the existing cardiac anatomy and the class it should
          use.
        </Typography.Paragraph>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Select
            style={{ flex: '1 1 200px', minWidth: 0 }}
            placeholder="Select existing cardiac anatomy"
            value={selectedExistingCardiacAnatomyIcid}
            onChange={setSelectedExistingCardiacAnatomyIcid}
            options={existingCardiacAnatomyOptions}
            showSearch
            optionFilterProp="label"
          />
          <ArrowRightOutlined style={{ color: COLORS.lightDark, flex: '0 0 auto' }} />
          <Select
            style={{ flex: '1 1 200px', minWidth: 0 }}
            placeholder="Select new cardiac anatomy"
            value={selectedSwapCardiacAnatomyClass}
            onChange={setSelectedSwapCardiacAnatomyClass}
            options={swapCardiacAnatomyOptions.map(option => ({
              value: option.uuid,
              label: option.name,
            }))}
            showSearch
            optionFilterProp="label"
          />
        </div>
      </Modal>

      <ExpandedSegmentationImageModal
        open={isExpandedImageModalOpen}
        onClose={() => setIsExpandedImageModalOpen(false)}
        imageSrc={frameFile || segmentation.fileUrl || (segmentation as any).file_url || ''}
        frameIndex={segmentation.frameIndex}
        allSegmentations={allSegmentations}
        visibleSegmentations={visibleSegmentations}
        onSegmentationToggle={onSegmentationToggle}
        isSegmentationAvailable={isSegmentationAvailable}
        segmentationClassOptions={segmentationClassOptions}
        getLocalDecision={getLocalDecision}
        removedReviews={removedReviews}
        onEditSegmentation={openLabelerEditForSegmentation}
      />

      <Card
        bordered={false}
        cover={segmentationCover()}
        actions={[
          <Tooltip title="Reject" key="reject">
            <Button
              type={'text'}
              icon={<BsHandThumbsDown />}
              disabled={onFrameReject === undefined && decision === Decision.REJECTED}
              onClick={() => {
                if (onFrameReject) {
                  onFrameReject();
                } else {
                  setIsRejectModalOpen(true);
                }
              }}
            />
          </Tooltip>,
          <Tooltip title="Accept" key="accept">
            <Button
              type={'text'}
              icon={<BsHandThumbsUp />}
              disabled={
                isSubmitting || frameReviewed || (!onFrameAccept && decision === Decision.ACCEPTED)
              }
              onClick={() => {
                if (onFrameAccept) {
                  onFrameAccept();
                } else {
                  setIsAcceptModalOpen(true);
                }
              }}
            />
          </Tooltip>,
          <Tooltip title="Undo Review" key="reverse">
            <Button
              type={'text'}
              icon={<UndoOutlined />}
              disabled={isReverseDisabled}
              onClick={() => {
                console.log('Reverse button clicked', {
                  segmentationIcids: allSegmentations.map(s => s.icid),
                  segmentationUuids: allSegmentations.map(s => s.uuid),
                });
                handleReverse();
              }}
            />
          </Tooltip>,
          <Tooltip title="Swap cardiac anatomy" key="swap-cardiac-anatomy">
            <Button
              type={'text'}
              aria-label="Swap cardiac anatomy"
              disabled={
                existingCardiacAnatomyOptions.length === 0 || swapCardiacAnatomyOptions.length === 0
              }
              icon={<BsShuffle />}
              onClick={() => {
                setIsSwapCardiacAnatomyModalOpen(true);
              }}
            />
          </Tooltip>,
          <Tooltip title="Add missing segmentation in Labeler" key="add-segmentation">
            <Button
              type={'text'}
              aria-label="Add missing segmentation in Labeler"
              icon={<PlusOutlined />}
              onClick={() => {
                setIsAddSegmentationModalOpen(true);
              }}
            />
          </Tooltip>,
        ]}
      >
        <Descriptions
          size="small"
          items={items}
          bordered
          column={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
        />
      </Card>
    </>
  );
};
export default SegmentationItem;
